import React, { useState } from "react";
import { Modal, Form, Input, Upload, Image, Button, Select } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import DebouncedSelect from "../../Component/DebouncedSelect";
import { getAllRecommendations } from "../../Services/recommendation";

const AddModal = ({ isModalVisible, handleOk, handleCancel, loading }) => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [previewImage, setPreviewImage] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);

  const { data: recommendationsData, isLoading: recommendationsLoading } = useQuery({
    queryKey: ["recommendations_all_for_destination", isModalVisible],
    queryFn: () => getAllRecommendations({ skip: 0, limit: 1000 }),
    enabled: isModalVisible,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const recommendationOptions = (recommendationsData?.recommendations || []).map((r) => ({
    label: r.name,
    value: r.id,
  }));

  const handleChange = ({ fileList }) => {
    setFileList(fileList.slice(-1));
    form.setFieldsValue({ image: fileList.length > 0 ? fileList[0] : null });
  };

  const handlePreview = async (file) => {
    const reader = new FileReader();
    reader.onload = (e) => setPreviewImage(e.target.result);
    reader.readAsDataURL(file.originFileObj);
    setPreviewOpen(true);
  };

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Select</div>
    </div>
  );

  const onFinish = (values) => {
    handleOk({
      ...values,
      recommendation_ids: values.recommendation_ids || [],
      image: fileList[0]?.originFileObj || null,
    });
  };

  useEffect(() => {
    form.resetFields();
    setFileList([]);
    setPreviewImage("");
  }, [form, isModalVisible]);

  return (
    <Modal
      title="Add New Recommended Destination"
      open={isModalVisible}
      footer={null} // Remove default buttons
      onCancel={() => {
        handleCancel();
        form.resetFields();
        setFileList([]);
        setPreviewImage("");
      }}
      maskClosable={false}
      style={{ top: 10 }}
    >
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item
          name="destination_text"
          label="Destination Place"
          rules={[
            { required: true, message: "Please enter Destination Place" },
          ]}
        >
          <Input placeholder="Enter Destination Place" />
        </Form.Item>

        <Form.Item
          name="destination_address"
          label="Destination Location"
          rules={[
            {
              required: true,
              message: "Please select an Destination Location",
            },
          ]}
        >
          <DebouncedSelect
            placeholder="Type at least 4 characters to search address"
            value={form.getFieldValue("destination_address")}
          />
        </Form.Item>
        <Form.Item
          name="recommendation_ids"
          label="Recommendations"
        >
          <Select
            mode="multiple"
            allowClear
            showSearch
            placeholder="Select recommendations"
            loading={recommendationsLoading}
            options={recommendationOptions}
            optionFilterProp="label"
            maxTagCount="responsive"
          />
        </Form.Item>
        <Form.Item
          name="image"
          label="Select Image"
          rules={[{ required: true, message: "Please upload an image" }]}
          dependencies={["image"]}
        >
          <Upload
            listType="picture-card"
            fileList={fileList}
            onPreview={handlePreview}
            onChange={handleChange}
            beforeUpload={() => false} // Prevent default upload behavior
            maxCount={1}
            accept=".jpg,.jpeg,.png,.svg"
            onRemove={async (file) => {
              return new Promise((resolve, reject) => {
                Modal.confirm({
                  title: "Are you sure you want to delete this image?",
                  okText: "Yes",
                  cancelText: "No",
                  onOk: () => {
                    resolve(true); // allow deletion
                  },
                  onCancel: () => {
                    resolve(false); // cancel deletion
                  },
                });
              });
            }}
          >
            {fileList.length >= 1 ? null : uploadButton}
          </Upload>
          <small>Recommended sizes: 640×360 px, 1280×720 px</small>
          <br />
          <small>Aspect ratio: 16:9</small>
          <br />
          <small>File size: 50–200 KB</small>
          {previewImage && (
            <Image
              wrapperStyle={{ display: "none" }}
              preview={{
                visible: previewOpen,
                onVisibleChange: (visible) => setPreviewOpen(visible),
                afterOpenChange: (visible) => !visible && setPreviewImage(""),
                toolbarRender: () => null,
              }}
              src={previewImage}
            />
          )}
        </Form.Item>
        <Form.Item>
          <div className="flex items-center justify-end gap-4">
            <Button onClick={handleCancel}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              Submit
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddModal;
